import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { saveAs } from 'file-saver';

import { SharedService } from 'src/app/modules/shared/_services/shared.service';
import { GlobalLoaderService } from 'src/app/modules/shared/_services/global-loader.service';
import { AuthService } from 'src/app/modules/auth/_services/auth.service';
import { PARAMS } from 'src/app/_configs/params';
import { environment } from 'src/environments/environment';
import { ThankyouModalComponent } from 'src/app/modules/shared/_components/thankyou-modal/thankyou-modal.component';
import { SigninWithOtpComponent } from 'src/app/modules/auth/_components/signin-with-otp/signin-with-otp.component';
import { StripMarginService } from 'src/app/modules/shared/_services/strip-margin.service';
import { DetailPageMetaService } from '../../_services/details-meta.service'
import { Subscription } from 'rxjs';

interface DataLayerEvent {
  event: string;
  [key: string]: any;
}
declare const dataLayer: DataLayerEvent[];
@Component({
  selector: 'app-tender-details',
  templateUrl: './tender-details.component.html',
  styleUrls: ['./tender-details.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class TenderDetailsComponent implements OnInit, OnDestroy {

  tender: any;
  sameOrganizationTenders: any[] = [];
  sameCategoryTenders: any[] = [];
  sameLocationTenders: any[] = [];
  TenderByKeywords: any[] = [];
  TenderByCities: any[] = [];

  showQueryTemplate = true;
  highlightSection = "overview";
  highlightSectionMobile = "overviewMobile";
  imageBasePath = environment.TENDER.IMAGE_BASE_URL;
  baseUrl = environment.TENDER.BASE_URL;
  queryFormGroup!: FormGroup;
  isSubmitted = false;
  currentPageUrl = this.sharedService.isBrowser ? window.location.href : '';
  isTooltipActive = false;
  tooltip = "Copy to Clipboard";
  tenderClosingDateDiff: any;
  oneAtATime = true;
  showTooltip = false;
  isLoggedIn: any;
  private userSubscription!: Subscription;
  isModalvis: boolean = false;
  @ViewChild(ThankyouModalComponent) Thankyoumodal!: ThankyouModalComponent;
  tenderView = false;
  showContent = false; // Flag to control content visibility
  stripMargin: boolean = true;
  shouldShowTrialBanner = false;
  isMobileCheck: any;
  stateSlug: string = '';
  private userData: any = null;
  isBrowser = false;
  constructor(private activatedRoute: ActivatedRoute, private sharedService: SharedService, private router: Router, private globalLoaderService: GlobalLoaderService,
    private authService: AuthService, private stripMarginService: StripMarginService, private detailPageMeta: DetailPageMetaService
  ) {
    this.isBrowser = this.sharedService.isBrowser;
    this.checkScreenSize();
    if (this.sharedService.isBrowser) {
      window.addEventListener('resize', () => this.checkScreenSize());
    }
    this.stripMarginService.stripMargin$.subscribe(
      (value) => {
        this.stripMargin = value;
      }
    );
  }

  private checkScreenSize() {
    if (this.sharedService.isBrowser) {
      if (typeof window !== 'undefined') {
        const isMobile = window.innerWidth <= 1199; // Adjust the breakpoint as needed
        if (isMobile) {
          this.isMobileCheck = true;
        } else {
          this.isMobileCheck = false;
        }
      } else {
        this.isMobileCheck = false;
      }
    }
  }

  @ViewChild(SigninWithOtpComponent) modal!: SigninWithOtpComponent;

  ngOnInit(): void {

    const url = this.router.url;
    const match = url.match(/\/([a-z-]+)-tender/);
    this.stateSlug = match?.[1] || '';

    if (url.includes('-tender')) {
      this.tenderView = true;
      this.showContent = true; // Show content only if URL is valid
      this.loadTenderDetails();
    } else {
      this.router.navigate(["page-not-found"]).then(() => {
        this.showContent = false;
      });
    }

    this.userSubscription = this.authService.user$.subscribe(user => {
      this.isLoggedIn = !!user;
      this.userData = user?.userData || null;
      this.checkTrialBanner();
    });
  }

  async checkTrialBanner() {
    const { show } = await this.authService.shouldShowTrialBanner();
    this.shouldShowTrialBanner = show;
  }

  private loadTenderDetails() {
    this.activatedRoute.data.subscribe(data => {
      const tenderData = data.tenderDetailsResolver;
      if (!tenderData) {
        this.router.navigate([PARAMS.ROUTING_PATHS.PAGE_NOT_FOUND]);
        return;
      }
      this.tender = tenderData[0].data?.tenderDetails;
      this.sameOrganizationTenders = tenderData[0].data?.tenderList;
      this.TenderByKeywords = data?.tendersResolver?.keywords?.data;
      this.TenderByCities = tenderData[1]?.data;
      this.detailPageMeta.setMetas(this.tender);
    });
  }

  openModalWithComponent() {
    this.isModalvis = true;
    // Delay setting the modal visibility to ensure view update
    setTimeout(() => {
      if (this.modal) {
        this.modal.isVisible = true; // Open the modal
        this.modal.isDownloadDoc = true; // Open the modal
      }
    }, 0);
  }

  get formattedDate(): string {
    return this.formatDate(this.tender?.bidSubmissionStartDate);
  }

  get bidSubmissionEndDateformattedDate(): string {
    return this.formatDate(this.tender?.bidSubmissionEndDate);
  }

  formatDate(date: string | Date): string {
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    };
    const dateObj = typeof date === 'string' ? this.parseDateString(date) : date;
    return dateObj && !isNaN(dateObj.getTime()) ? dateObj.toLocaleDateString('en-US', options) : 'Invalid date';
  }

  private parseDateString(date: string): Date {
    const [datePart, timePart, period] = date.split(/[\s:]+/);
    const [year, month, day] = datePart.split('-').map(Number);
    let [hours, minutes] = [Number(timePart), 0];
    if (period === 'PM' && hours < 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return new Date(year, month - 1, day, hours, minutes);
  }

  downloadDoc(doc: any) {
    this.globalLoaderService.setLoaderState(true);
    this.sharedService.downloadFile(doc?.url).subscribe(res => {
      this.globalLoaderService.setLoaderState(false);
      saveAs(new Blob([res], { type: res.type }), doc?.name);
    }, () => {
      this.globalLoaderService.setLoaderState(false);
    });
  }

  navigateToSection(section: any, event: any) {
    if (this.sharedService.isBrowser) {
      section.scrollIntoView({ behavior: 'smooth', block: 'center', inline: "nearest" });
      document.querySelectorAll('.nav-link').forEach(el => el.classList.remove('active'));
      event.currentTarget.classList.add('active');
    }
  }

  navigateToTender(link: string, event: MouseEvent) {
    event.preventDefault();
    this.router.navigate([`/${link}-tenders/active`]);
  }

  navigate(url: string) {
    this.router.navigateByUrl(url);
  }

  copyTextToClipboard() {
    this.tooltip = PARAMS.STATIC_PARAMS.TOOLTIP_COPIED;
    if (this.sharedService.isBrowser) {
      navigator.clipboard.writeText(this.currentPageUrl).catch(err => console.error('Failed to copy URL to clipboard:', err));
    }
    this.isTooltipActive = true;
  }

  shareTenderDetails(hrefUrl: string, medium: string) {
    if (this.sharedService.isBrowser) {
      window.open(hrefUrl, '_blank');
    }
  }

  shareTenderDetailsWhatsapp() {
    const whatsappUrl = this.isMobile()
      ? `whatsapp://send?text=${encodeURIComponent(this.currentPageUrl)}`
      : `https://web.whatsapp.com/send?text=${encodeURIComponent(this.currentPageUrl)}`;
    if (this.sharedService.isBrowser) {
      window.open(whatsappUrl, '_blank');
    }
  }

  private isMobile(): boolean {
    return /Mobi|Android/i.test(navigator.userAgent);
  }

  ThankyouMessagePopup() {
    if (!this.userData) {
      console.warn('No user data available.');
      return;
    }
    const requestData = {
      leadId: this.userData.leadId,
      summary: this.tender?.summary,
      tenderRefId: this.tender?.tenderRefId,
      workDescription: this.tender?.workDescription
    };

    this.sharedService.subscribeTenders(requestData).subscribe(res => {
      if (res.code === 200) {
        this.Thankyoumodal.isThankyouVisible = true;
        this.pushNormalUser();
      }
    });
  }

  pushNormalUser() {
    if (typeof dataLayer !== 'undefined') {
      dataLayer.push({
        'event': 'tender_submit_detail_form'
      });
    } else {
      console.warn("dataLayer is not defined.");
    }
  }

  tenderdetails: any
  leadFormPopup(tenderDetails: any) {
    this.isModalvis = true;
    setTimeout(() => {
      if (this.modal) {
        this.modal.isVisible = true; // Open the modal
        this.modal.isDownloadDoc = true; // Open the modal
        this.tenderdetails = tenderDetails;
      }
    }, 0);
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.scrollTop(), 0);
  }

  scrollTop() {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }

  get formControls() {
    return this.queryFormGroup.controls;
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

}